import { useCallback, useMemo, useState } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { isApiError } from '@/apis/apiErrors';
import {
  fetchSensorModalPreferences,
  updateSensorModalPreferences,
  type SensorModalPreferences,
} from '@/apis/preferences';
import {
  fetchShipmentReportDetail,
  type SensorReportShipment,
} from '@/apis/shipmentEvents';
import {
  fetchTelemetrySeries,
  fetchTelemetrySummary,
  type SeriesBySensor,
  type TelemetrySummary,
  type TelemetryVital,
} from '@/apis/telemetry';
import {
  ChunkName,
  measureGroups,
  seriesChunks,
  type SeriesChunk,
} from './lib/chunks';
import { availableCharts, DEFAULT_CHARTS } from './lib/measures';
import { stitchSeries } from './lib/stitch';

/** Key prefix matching every cached telemetry summary for one shipment, logged-in or shared. */
export const telemetrySummaryShipmentKey = (shipmentId: string) =>
  ['telemetry-summary', shipmentId] as const;

/** React Query key for one shipment's telemetry summary. */
export const telemetrySummaryKey = (shipmentId: string, shareToken?: string) =>
  [...telemetrySummaryShipmentKey(shipmentId), shareToken ?? null] as const;

/** A callback that refetches a shipment's summary, for pages that already reload the shipment on a timer. */
export const useRefreshTelemetrySummary = (): ((
  shipmentId: string
) => Promise<void>) => {
  const queryClient = useQueryClient();
  return useCallback(
    (shipmentId: string) =>
      queryClient.invalidateQueries({
        queryKey: telemetrySummaryShipmentKey(shipmentId),
      }),
    [queryClient]
  );
};

/** Loads a shipment's telemetry summary once per mount, with no automatic refetching. */
export const useTelemetrySummary = (
  shipmentId: string | undefined,
  { enabled, shareToken }: { enabled: boolean; shareToken?: string }
): UseQueryResult<TelemetrySummary> =>
  useQuery({
    queryKey: telemetrySummaryKey(shipmentId ?? '', shareToken),
    queryFn: () => fetchTelemetrySummary(shipmentId ?? '', shareToken),
    enabled: enabled && Boolean(shipmentId),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

/** Loads a shipment's detail once per mount, for a Sensor modal opened without the drawer's. */
export const useShipmentReportDetail = (
  shipmentId: string,
  { enabled, shareToken }: { enabled: boolean; shareToken?: string }
): UseQueryResult<SensorReportShipment> =>
  useQuery({
    queryKey: ['shipment-report-detail', shipmentId, shareToken ?? null],
    queryFn: () => fetchShipmentReportDetail(shipmentId, shareToken),
    enabled,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

/** React Query key for one series request: a chunk's window of the shipment's readings for a group of measures. */
export const telemetrySeriesKey = (
  shipmentId: string,
  chunk: SeriesChunk,
  measures: readonly TelemetryVital[],
  shareToken?: string
) =>
  [
    'telemetry-series',
    shipmentId,
    chunk.name,
    chunk.from,
    chunk.to,
    measures.join(','),
    shareToken ?? null,
  ] as const;

/** Load state of one chunk; `tooLarge` is a 422, shown as "not loaded" rather than "no readings". */
export enum ChunkStatus {
  Loading = 'loading',
  Ready = 'ready',
  TooLarge = 'tooLarge',
  Failed = 'failed',
}

/** Load state of each chart's readings in each chunk. */
export type ChunkMeasureStatus = Partial<
  Record<ChunkName, Partial<Record<TelemetryVital, ChunkStatus>>>
>;

/** Stitched readings for the selected charts, plus each chunk's, each chart's operational-window, and each chart's per-chunk load state. */
export interface TelemetrySeriesState {
  series: SeriesBySensor;
  chunks: Partial<Record<ChunkName, ChunkStatus>>;
  operational: Partial<Record<TelemetryVital, ChunkStatus>>;
  byChunk: ChunkMeasureStatus;
}

/** How long unused readings stay cached after the modal closes, so a quick reopen doesn't refetch. */
export const SERIES_CACHE_MS = 30 * 60 * 1000;

const chunkStatus = (
  results: readonly { isPending: boolean; error: unknown }[]
): ChunkStatus => {
  if (
    results.some((r) => isApiError(r.error) && r.error.response?.status === 422)
  ) {
    return ChunkStatus.TooLarge;
  }
  if (results.some((r) => r.error)) return ChunkStatus.Failed;
  return results.some((r) => r.isPending)
    ? ChunkStatus.Loading
    : ChunkStatus.Ready;
};

/**
 * Loads raw readings once per chunk: every available vital for the operational window in one request
 * (so the modal knows which charts have readings there), then the selected charts' before-pickup and
 * after-delivery tails in the background, stitched in time order.
 */
export const useTelemetrySeries = (
  shipmentId: string,
  summary: TelemetrySummary | undefined,
  selected: readonly TelemetryVital[],
  { now, shareToken }: { now: number; shareToken?: string }
): TelemetrySeriesState => {
  const { chunks, requests } = useMemo(() => {
    const windows = summary ? seriesChunks(summary.anchors, now) : [];
    const available = availableCharts(summary);
    const groups = measureGroups(selected, available);
    return {
      chunks: windows,
      requests: windows.flatMap((chunk) =>
        chunk.name === ChunkName.Operational
          ? available.length > 0
            ? [{ chunk, measures: available }]
            : []
          : groups.map((measures) => ({ chunk, measures }))
      ),
    };
  }, [summary, now, selected]);
  const operationalKeys = requests
    .filter((r) => r.chunk.name === ChunkName.Operational)
    .map((r) =>
      telemetrySeriesKey(shipmentId, r.chunk, r.measures, shareToken)
    );
  const queryClient = useQueryClient();
  const combine = useCallback(
    (
      results: readonly {
        data?: SeriesBySensor;
        isPending: boolean;
        error: unknown;
      }[]
    ) => {
      const state: TelemetrySeriesState = {
        series: {},
        chunks: {},
        operational: {},
        byChunk: {},
      };
      results.forEach((result, i) => {
        const { chunk, measures } = requests[i];
        const forChunk = (state.byChunk[chunk.name] ??= {});
        for (const measure of measures) {
          forChunk[measure] = chunkStatus([result]);
          if (chunk.name === ChunkName.Operational) {
            state.operational[measure] = forChunk[measure];
          }
        }
      });
      const loaded: SeriesBySensor[] = [];
      for (const chunk of chunks) {
        const forChunk = results.filter(
          (_, i) => requests[i].chunk.name === chunk.name
        );
        state.chunks[chunk.name] = chunkStatus(forChunk);
        for (const result of forChunk) {
          if (result.data) loaded.push(result.data);
        }
      }
      state.series = stitchSeries(loaded);
      return state;
    },
    [chunks, requests]
  );
  const operationalSettled = operationalKeys.every((key) => {
    const state = queryClient.getQueryState(key);
    return state?.status === 'success' || state?.status === 'error';
  });

  return useQueries({
    queries: requests.map(({ chunk, measures }) => ({
      queryKey: telemetrySeriesKey(shipmentId, chunk, measures, shareToken),
      queryFn: () => fetchChunk(shipmentId, chunk, measures, shareToken),
      enabled: chunk.name === ChunkName.Operational || operationalSettled,
      staleTime: Infinity,
      gcTime: SERIES_CACHE_MS,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
    })),
    combine,
  });
};

const fetchChunk = async (
  shipmentId: string,
  chunk: SeriesChunk,
  measures: readonly TelemetryVital[],
  shareToken?: string
): Promise<SeriesBySensor> =>
  (
    await fetchTelemetrySeries(
      shipmentId,
      {
        measures,
        from: chunk.from,
        to: chunk.to,
        includeEnd: chunk.includeEnd,
      },
      shareToken
    )
  ).series;

/** React Query key for the logged-in user's Sensor modal preferences. */
export const sensorModalPreferencesKey = [
  'preferences',
  'sensor-modal',
] as const;

/** Preferences a user starts with, and what share-link viewers always use; mirrors `SENSOR_MODAL_DEFAULTS` in backend/preferences/serializers.py. */
export const DEFAULT_PREFERENCES: SensorModalPreferences = {
  charts: [...DEFAULT_CHARTS],
  show_events: false,
};

interface SaveState {
  latestEdit: number;
  confirmed: SensorModalPreferences | null;
}

const saveStates = new WeakMap<QueryClient, SaveState>();

const saveStateFor = (queryClient: QueryClient): SaveState => {
  const existing = saveStates.get(queryClient);
  if (existing) return existing;
  const created: SaveState = { latestEdit: 0, confirmed: null };
  saveStates.set(queryClient, created);
  return created;
};

/** The current preferences, whether the saved ones have loaded, and a function that saves changes to them. */
export interface SensorModalPreferencesState {
  preferences: SensorModalPreferences;
  loaded: boolean;
  update: (changes: Partial<SensorModalPreferences>) => void;
}

/**
 * Loads and saves Sensor modal preferences. Saves apply optimistically and run one at a time, in
 * order; a response never overwrites a newer local edit, and a failed save rolls back only when no
 * newer edit is pending. Edit order is tracked per query client, so it survives the modal closing and
 * reopening. Share-link viewers keep choices in memory and never call the API.
 */
export const useSensorModalPreferences = ({
  anonymous,
}: {
  anonymous: boolean;
}): SensorModalPreferencesState => {
  const queryClient = useQueryClient();
  const [local, setLocal] =
    useState<SensorModalPreferences>(DEFAULT_PREFERENCES);
  const saves = saveStateFor(queryClient);

  const query = useQuery({
    queryKey: sensorModalPreferencesKey,
    queryFn: fetchSensorModalPreferences,
    enabled: !anonymous,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const { mutate } = useMutation({
    scope: { id: 'sensor-modal-preferences' },
    mutationFn: ({
      changes,
    }: {
      changes: Partial<SensorModalPreferences>;
      edit: number;
    }) => updateSensorModalPreferences(changes),
    onSuccess: (saved, { edit }) => {
      saves.confirmed = saved;
      if (edit === saves.latestEdit) {
        queryClient.setQueryData(sensorModalPreferencesKey, saved);
      }
    },
    onError: (error, { edit }) => {
      console.error(error);
      if (edit !== saves.latestEdit) return;
      if (saves.confirmed) {
        queryClient.setQueryData(sensorModalPreferencesKey, saves.confirmed);
      } else {
        const _ = queryClient.invalidateQueries({
          queryKey: sensorModalPreferencesKey,
        });
      }
    },
  });

  const update = useCallback(
    (changes: Partial<SensorModalPreferences>) => {
      if (anonymous) {
        setLocal((prev) => ({ ...prev, ...changes }));
        return;
      }
      const _ = queryClient.cancelQueries({
        queryKey: sensorModalPreferencesKey,
      });
      const loaded = queryClient.getQueryData<SensorModalPreferences>(
        sensorModalPreferencesKey
      );
      if (saves.latestEdit === 0 && loaded) saves.confirmed = loaded;
      const current = loaded ?? DEFAULT_PREFERENCES;
      saves.latestEdit += 1;
      queryClient.setQueryData(sensorModalPreferencesKey, {
        ...current,
        ...changes,
      });
      mutate({ changes, edit: saves.latestEdit });
    },
    [anonymous, mutate, queryClient, saves]
  );

  return {
    preferences: anonymous ? local : (query.data ?? DEFAULT_PREFERENCES),
    loaded: anonymous || query.isSuccess,
    update,
  };
};
